/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import { TENANTS } from "../../../constants"
import { extractList, getCountryFromLanguage } from "../../../core/service/BusinessLogicService"
import { isItalianTenant } from "../../../core/utils"
import { sleep } from "../../../core/utils.common"
import { FOREIGN_DEPARTEMENT } from "../../IdentificationPage/constants/identificationPageConstants"
import {
  DATA_INIZIO_LAVARO_NO_DISPLAY_PROFESSIONS,
  DATE_FORMAT,
  DATORE_CITTA_PROVINCIA_NO_DISPLAY_PROFESSIONS,
  IDENTITY_DOCUMENT_TYPE_PATENTE,
  PROFESSIONAL_USE_PERS,
  PROFESSION_WITHOUT_BUDGET_INFO_NL,
  REDITTO_MENSILE_IN_EURO_NO_DISPLAY_PROFESSIONS,
  TIPO_DI_CONTRATTO_DETERMINATO_INDETERMINATO_PROFESSIONS,
  TIPO_DI_CONTRATTO_DETERMINATO_PROFESSIONS,
  TIPO_DI_CONTRATTO_INDETERMINATO_PROFESSIONS,
  WORKING_SECTOR_AUTONOMO,
  WORKING_SECTOR_UNIVERSITY_STUDENT_CISCLOS_NOTWORKING
} from "../constants/ClientPageConstants"
import { MARIED_VALUE } from "../constants/MaritalSituations"
import { getBicNl } from "../validator/bicNlStore"
import axios from "axios"
import CodiceFiscale from "codice-fiscale-js"
import createDecorator from "final-form-calculate"
import moment from "moment"

const postalCodeDecorator = () => {
  return {
    field: "coordinates.postalCode",
    updates: {
      "coordinates.city": (ignoredValue, allValues) => {
        return null
      },
      "coordinates.province": (ignoredValue, allValues) => {
        return null
      }
    }
  }
}

const cityDecorator = (tunnelData) => {
  return {
    field: "coordinates.city",
    updates: {
      "coordinates.province": async (ignoredValue, allValues) => {
        if (allValues.coordinates.city && !isNaN(allValues.coordinates.city?.value)) {
          let response = await axios.get(
            tunnelData.apiUrl +
              "middle-business-service/api/v2/repository/department-by-city/" +
              allValues.coordinates.city.value,
            { headers: { Authorization: `Bearer ${tunnelData.access_token}`, Accept: "*/*" } }
          )
          return response.data
        } else {
          // this else is mandatory otherwise it will return '' by default
          return allValues.coordinates.province
        }
      }
    }
  }
}

const citywithPostalCodeDecorator = () => {
  return {
    field: "coordinates.postalCode",
    updates: {
      "coordinates.city": (ignoredValue, allValues) => {
        if (!allValues.coordinates.postalCode) {
          return null
        } else {
          // this else is mandatory otherwise it will return '' by default
          return allValues.coordinates.city
        }
      }
    }
  }
}

const cityLavoroDecorator = (tunnelData) => {
  if (getCountryFromLanguage(tunnelData.data.order.orderLanguageTag) !== "IT") {
    return { field: "", updates: {} }
  }
  return {
    field: "situation.companyCity",
    updates: {
      "situation.companyProvince": async (ignoredValue, allValues) => {
        if (allValues.situation.companyCity) {
          let response = await axios.get(
            tunnelData.apiUrl +
              "middle-business-service/api/v2/repository/department-by-city/province/" +
              allValues.situation.companyCity.value,
            { headers: { Authorization: `Bearer ${tunnelData.access_token}`, Accept: "*/*" } }
          )
          return response.data
        }
      }
    }
  }
}
const fisalNumberDecorator = (tunnelData) => {
  if (getCountryFromLanguage(tunnelData?.data?.order?.orderLanguageTag) !== "IT") {
    return { field: "", updates: {} }
  }
  return {
    field: "identity.taxNumber",
    updates: {
      "identity.birthDate": (ignoredValue, allValues) => {
        try {
          let codiceFiscale = new CodiceFiscale(allValues.identity.taxNumber)
          let day = ("" + codiceFiscale.day).length === 1 ? "0" + codiceFiscale.day : codiceFiscale.day
          let month = ("" + codiceFiscale.month).length === 1 ? "0" + codiceFiscale.month : codiceFiscale.month
          return moment(day + "/" + month + "/" + codiceFiscale.year, "DD/MM/YYYY").format(
            DATE_FORMAT.identity.birthDate["IT"]
          )
        } catch (error) {
          return null
        }
      },
      "identity.birthCountry": async (ignoredValue, allValues) => {
        try {
          let codiceFiscale = new CodiceFiscale(allValues.identity.taxNumber)
          if (codiceFiscale.birthplace.prov !== FOREIGN_DEPARTEMENT) {
            let response = await axios.get(
              tunnelData.apiUrl +
                "middle-business-service/api/v2/repository/country-cadastral?journeyCountryCode=IT&countryCode=IT",
              {
                headers: {
                  Authorization: `Bearer ${tunnelData.access_token}`,
                  Accept: "*/*"
                }
              }
            )
            return response.data
          } else {
            let response = await axios.get(
              tunnelData.apiUrl +
                "middle-business-service/api/v2/repository/country-cadastral/foreign-country?cadastralCode=" +
                codiceFiscale.birthplace.cc,
              {
                headers: {
                  Authorization: `Bearer ${tunnelData.access_token}`,
                  Accept: "*/*"
                }
              }
            )
            return response.data
          }
        } catch (error) {
          return null
        }
      },
      "identity.birthDepartment": async (ignoredValue, allValues) => {
        try {
          let codiceFiscale = new CodiceFiscale(allValues.identity.taxNumber)
          let response = await axios.get(
            tunnelData.apiUrl +
              "middle-business-service/api/v2/repository/department-cadastral?cadastralCode=" +
              codiceFiscale.birthplace.cc,
            {
              headers: {
                Authorization: `Bearer ${tunnelData.access_token}`,
                Accept: "*/*"
              }
            }
          )
          return response.data
        } catch (error) {
          return null
        }
      },
      "identity.birthCity": async (ignoredValue, allValues) => {
        try {
          let codiceFiscale = new CodiceFiscale(allValues.identity.taxNumber)
          if (codiceFiscale.birthplace.prov !== FOREIGN_DEPARTEMENT) {
            let response = await axios.get(
              tunnelData.apiUrl +
                "middle-business-service/api/v2/repository/city-cadastral?cadastralCode=" +
                codiceFiscale.birthplace.cc,
              {
                headers: {
                  Authorization: `Bearer ${tunnelData.access_token}`,
                  Accept: "*/*"
                }
              }
            )
            return response.data
          } else {
            let response = await axios.get(
              tunnelData.apiUrl +
                "middle-business-service/api/v2/repository/country-cadastral/foreign-country?cadastralCode=" +
                codiceFiscale.birthplace.cc,
              {
                headers: {
                  Authorization: `Bearer ${tunnelData.access_token}`,
                  Accept: "*/*"
                }
              }
            )
            return response.data.label
          }
        } catch (error) {
          return null
        }
      }
    }
  }
}
const birthCountryDecorator = () => {
  return {
    field: "identity.birthCountry",
    updates: {
      "identity.birthDepartment": (ignoredValue, allValues) => {
        if (!allValues.identity.birthCountry) {
          return null
        } else {
          // this else is mandatory otherwise it will return '' by default
          return allValues.identity.birthDepartment
        }
      }
    }
  }
}

const birthDepartmentDecorator = () => {
  return {
    field: "identity.birthDepartment",
    updates: {
      "identity.birthCity": (ignoredValue, allValues) => {
        if (!allValues.identity.birthDepartment) {
          return null
        } else {
          // this else is mandatory otherwise it will return '' by default
          return allValues.identity.birthCity
        }
      }
    }
  }
}

const citizenShipSimplifiedDecorator = () => {
  return {
    field: "identityDocuments.citizenshipSimplified",
    updates: {
      "identityDocuments.citizenshipExtended": (ignoredValue, allValues) => {
        if (allValues.identityDocuments.citizenshipSimplified?.value === 1) {
          return {
            value: "IT",
            label: "ITALIA"
          }
        } else {
          // this else is mandatory otherwise it will return '' by default
          return allValues.identityDocuments.citizenshipExtended
        }
      }
    }
  }
}

const phoneNumberDecorator = () => {
  return {
    field: "coordinates.phoneNumber",
    updates: {
      "coordinates.phoneNumber": (ignoredValue, allValues) => allValues.coordinates.phoneNumber
    }
  }
}

const identityDocumentPlaceOfIssueDecorator = (tunnelData) => {
  return {
    field: "identityDocuments.documentPlaceOfIssue",
    updates: {
      "identityDocuments.documentProvinceOfIssue": async (ignoredValue, allValues) => {
        let response = await axios.get(
          `${tunnelData.apiUrl}middle-business-service/api/v2/repository/department-by-city/province-code/${allValues?.identityDocuments?.documentPlaceOfIssue?.value}`,
          { headers: { Authorization: `Bearer ${tunnelData.access_token}`, Accept: "*/*" } }
        )
        return response.data
      }
    }
  }
}

const workingSituationDecorator = () => {
  return {
    field: "situation.workingSituation",
    updates: {
      "situation.endContractDate": async (ignoredValue, allValues) => {
        if (allValues.situation.workingSituation?.value === "IT2") {
          return null
        }
        return allValues.situation.endContractDate
      }
    }
  }
}

const identityDocumentsDriverLicenseNumberDecorator = () => {
  return {
    field: "identityDocuments.identityDocumentType",
    updates: {
      "identityDocuments.identityDocumentsDriverLicenseNumber": async (ignoredValue, allValues) => {
        if (IDENTITY_DOCUMENT_TYPE_PATENTE.includes(allValues.identityDocuments.identityDocumentType?.value)) {
          return allValues.identityDocuments.identityDocumentsDriverLicenseNumber
        }
        return null
      }
    }
  }
}

const netMonthlyIncomeDecorator = () => {
  return {
    field: "situation.netMonthlyIncome",
    updates: {
      "budget.netMonthlyIncome": (ignoredValue, allValues) => {
        return allValues.situation.netMonthlyIncome
      }
    }
  }
}

const numberMonthsIncomeDecorator = (tunnelData) => {
  return {
    field: "budget.netMonthlyIncome",
    updates: {
      "budget.numberMonthsIncome": async (_netMonthlyIncomeValue, allValues) => {
        let response = await axios.get(
          `${tunnelData.apiUrl}middle-business-service/api/v2/repository/income-number-month/${getCountryFromLanguage(
            tunnelData.data.order.orderLanguageTag
          )}`,
          { headers: { Authorization: `Bearer ${tunnelData.access_token}`, Accept: "*/*" } }
        )
        const selectedOptionFromTunnelData = extractList(response.data).filter(
          (item) => item.value === allValues.budget.numberMonthsIncome?.toString()
        )

        return selectedOptionFromTunnelData[0] || allValues.budget.numberMonthsIncome?.value?.toString()
      }
    }
  }
}

const detailsIncomeDecorator = () => {
  return {
    field: "budget.incomeOrigin",
    updates: {
      "budget.showDetailsIncome": (ignoredValue, allValues) => {
        if (allValues.budget.incomeOrigin?.label === "Otros") {
          return true
        }
        return false
      }
    }
  }
}

const showProfessionDecorator = () => {
  return {
    field: "situation.showProfession",
    updates: {
      "situation.profession": async (ignoredValue, allValues) => {
        if (!allValues.situation.showProfession) {
          return null
        }
        await sleep(2000)
        return allValues.situation.profession
      }
    }
  }
}

const showProfessionelUseDecorator = () => {
  return {
    field: "situation.showProfessionelUse",
    updates: {
      "situation.professionalUse": (ignoredValue, allValues) => {
        if (!allValues.situation.showProfessionelUse) {
          return null
        }
        return allValues.situation.professionalUse
      }
    }
  }
}

const showLineOfBusinessUseDecorator = () => {
  return {
    field: "situation.showLineOfBusiness",
    updates: {
      "situation.lineOfBusiness": (ignoredValue, allValues) => {
        if (!allValues.situation.showLineOfBusiness) {
          return null
        }
        return allValues.situation.lineOfBusiness
      }
    }
  }
}

const showContryActivityInfosUseDecorator = () => {
  return {
    field: "situation.showContryActivityInfo",
    updates: {
      "situation.countryOfActivity": (ignoredValue, allValues) => {
        if (!allValues.situation.showContryActivityInfo) {
          return null
        }
        return allValues.situation.countryOfActivity
      }
    }
  }
}

const showCompanyNameDecorator = () => {
  return {
    field: "situation.showCompanyName",
    updates: {
      "situation.companyName": async (ignoredValue, allValues) => {
        await sleep(2000)
        if (!allValues.situation.showCompanyName) {
          return null
        }
        return allValues.situation.companyName
      }
    }
  }
}

const professionalUseDecorator = (tunnelData) => {
  return {
    field: "situation.professionalUse",
    updates: {
      "situation.showContryActivityInfo": (ignoredValue, allValues) => {
        if (allValues.situation.professionalUse?.label === "Profesional") {
          return true
        }
      },
      "situation.hideLineOfBusiness": (ignoredValue, allValues) => {
        if (
          allValues.situation.workingSector?.label === WORKING_SECTOR_AUTONOMO &&
          allValues.situation.professionalUse?.label === PROFESSIONAL_USE_PERS
        ) {
          return true
        }
      }
    }
  }
}

const sectorwithProfessionDecorator = (tunnelData) => {
  return {
    field: "situation.profession",
    updates: {
      "situation.showContryActivityInfo": (ignoredValue, allValues) => {
        if (
          allValues.situation.workingSector?.label === "Privado" &&
          allValues.situation.profession?.label === "Directivo"
        ) {
          return true
        }
        return false
      },
      "situation.showCompanyName": (professionFieldValue, allValues) => {
        const hideCompanyNameProfessions = ["PENSIONATO", "CASALINGA, SENZA PROFESSIONE", "STUDENTE"]
        if (
          isItalianTenant(tunnelData.tenant) &&
          allValues.situation.profession?.value &&
          !hideCompanyNameProfessions.includes(allValues.situation.profession.label)
        ) {
          return true
        }

        if (
          tunnelData.tenant === TENANTS.SAMSUNG_ES &&
          (allValues.situation.workingSector?.label === "Autónomo" ||
            (allValues.situation.workingSector?.label === "Privado" && professionFieldValue?.label === "Directivo"))
        ) {
          return true
        }

        return false
      }
    }
  }
}

const enableIngressoDecorator = (tunnelData) => {
  return {
    field: "budget.netMonthlyIncome",
    updates: {
      "budget.netMonthlyIncome": (ignoredValue, allValues) => {
        if (!allValues.budget.netMonthlyIncome) {
          return null
        }
        return allValues.budget.netMonthlyIncome
      }
    }
  }
}

const showIncomeOriginDecorator = (tunnelData) => {
  return {
    field: "budget.showIncomeOrigin",
    updates: {
      "budget.incomeOrigin": (ignoredValue, allValues) => {
        if (!allValues.budget.showIncomeOrigin) {
          return null
        }
        return allValues.budget.incomeOrigin
      }
    }
  }
}

const showDetailsIncomeDecorator = (tunnelData) => {
  return {
    field: "budget.showDetailsIncome",
    updates: {
      "budget.detailOfIncome": (ignoredValue, allValues) => {
        if (!allValues.budget.showDetailsIncome) {
          return null
        }
        return allValues.budget.detailOfIncome
      }
    }
  }
}

const sectorDecorator = (tunnelData) => {
  return {
    field: "situation.workingSector",
    updates: {
      "situation.showProfession": (ignoredValue, allValues) => {
        if (
          allValues.situation.workingSector?.label === "Autónomo" ||
          allValues.situation.workingSector?.label === "Privado" ||
          allValues.situation.workingSector?.label === "Público" ||
          tunnelData?.tenant !== "samsung_es"
        ) {
          return true
        }
      },
      "situation.showProfessionelUse": (ignoredValue, allValues) => {
        if (allValues.situation.workingSector?.label === "Autónomo") {
          return true
        }
      },
      "situation.showCompanyName": (ignoredValue, allValues) => {
        if (
          allValues.situation.workingSector?.label === "Autónomo" ||
          allValues.situation.workingSector?.label === "Privado"
        ) {
          return true
        }
      },
      "situation.showLineOfBusiness": (ignoredValue, allValues) => {
        if (
          allValues.situation.workingSector?.label === "Autónomo" ||
          allValues.situation.workingSector?.label === "Privado" ||
          allValues.situation.workingSector?.label === "Público"
        ) {
          return true
        }
      },
      "situation.showContryActivityInfo": (ignoredValue, allValues) => {
        if (allValues.situation.workingSector?.label !== "Autónomo") {
          return false
        }
      },
      "budget.showIncomeOrigin": (ignoredValue, allValues) => {
        if (WORKING_SECTOR_UNIVERSITY_STUDENT_CISCLOS_NOTWORKING.includes(allValues.situation.workingSector?.label)) {
          return true
        }
        return false
      },
      "budget.enableIngresso": (ignoredValue, allValues) => {
        if (
          allValues.situation.workingSector?.label === "Autónomo" ||
          allValues.situation.workingSector?.label === "Privado" ||
          allValues.situation.workingSector?.label === "Público"
        ) {
          return true
        }
        return false
      }
    }
  }
}

const professionDecorator = (tunnelData) => {
  return {
    field: "situation.profession",
    updates: {
      "situation.hideEmploymentDate": (ignoredValue, allValues) => {
        return DATA_INIZIO_LAVARO_NO_DISPLAY_PROFESSIONS.includes(allValues.situation.profession?.value)
      },
      "situation.employmentDate": (ignoredValue, allValues) => {
        if (DATA_INIZIO_LAVARO_NO_DISPLAY_PROFESSIONS.includes(allValues.situation.profession?.value)) {
          return null
        }
        if (PROFESSION_WITHOUT_BUDGET_INFO_NL.includes(allValues.situation.profession?.label)) {
          return null
        }
        return allValues.situation.employmentDate
      },
      "situation.workingSituation": (ignoredValue, allValues) => {
        let country = getCountryFromLanguage(tunnelData.data.order.orderLanguageTag)
        if (TIPO_DI_CONTRATTO_INDETERMINATO_PROFESSIONS.includes(allValues.situation.profession?.value)) {
          return "IT2"
        }
        if (TIPO_DI_CONTRATTO_DETERMINATO_PROFESSIONS.includes(allValues.situation.profession?.value)) {
          return "IT1"
        }
        if (TIPO_DI_CONTRATTO_DETERMINATO_INDETERMINATO_PROFESSIONS.includes(allValues.situation.profession?.value)) {
          return allValues.situation.workingSituation?.value
        }
        if (country === "IT") {
          return null
        } else {
          return allValues.situation.workingSituation
        }
      },
      "situation.disableWorkingSituation": (ignoredValue, allValues) => {
        if (
          TIPO_DI_CONTRATTO_DETERMINATO_PROFESSIONS.includes(allValues.situation.profession?.value) ||
          TIPO_DI_CONTRATTO_INDETERMINATO_PROFESSIONS.includes(allValues.situation.profession?.value)
        ) {
          return false
        }
        if (TIPO_DI_CONTRATTO_DETERMINATO_INDETERMINATO_PROFESSIONS.includes(allValues.situation.profession?.value)) {
          return true
        }
      },
      "situation.hideCompanyInfo": (ignoredValue, allValues) => {
        return DATORE_CITTA_PROVINCIA_NO_DISPLAY_PROFESSIONS.includes(allValues.situation.profession?.value)
      },
      "situation.companyName": (ignoredValue, allValues) => {
        if (DATORE_CITTA_PROVINCIA_NO_DISPLAY_PROFESSIONS.includes(allValues.situation.profession?.value)) {
          return null
        }
        return allValues.situation.companyName
      },
      "situation.companyCity": (ignoredValue, allValues) => {
        if (DATORE_CITTA_PROVINCIA_NO_DISPLAY_PROFESSIONS.includes(allValues.situation.profession?.value)) {
          return null
        }
        return allValues.situation.companyCity
      },
      "situation.companyProvince": (ignoredValue, allValues) => {
        if (DATORE_CITTA_PROVINCIA_NO_DISPLAY_PROFESSIONS.includes(allValues.situation.profession?.value)) {
          return null
        }
        return allValues.situation.companyProvince
      },
      "situation.hideNetMonthlyIncome": (ignoredValue, allValues) => {
        return REDITTO_MENSILE_IN_EURO_NO_DISPLAY_PROFESSIONS.includes(allValues.situation.profession?.value)
      },
      "situation.netMonthlyIncome": (ignoredValue, allValues) => {
        if (REDITTO_MENSILE_IN_EURO_NO_DISPLAY_PROFESSIONS.includes(allValues.situation.profession?.value)) {
          return null
        }
        return allValues.situation.netMonthlyIncome
      },
      hideEmploymentInfo: (ignoredValue, allValues) => {
        if (PROFESSION_WITHOUT_BUDGET_INFO_NL.includes(allValues.situation.profession?.label)) {
          return true
        }
        return false
      }
    }
  }
}

const ibanDecorator = (tunnelData) => {
  return {
    field: "coordinates.iban",
    updates: {
      "coordinates.iban": (ignoredValue, allValues) => {
        if (getCountryFromLanguage(tunnelData.data.order.orderLanguageTag) === "ES") {
          return allValues.coordinates.iban?.replace(/\s/g, "")
        }
        return allValues.coordinates.iban
      },
      "coordinates.bic": (ignoredValue, allValues) => {
        if (getCountryFromLanguage(tunnelData.data.order.orderLanguageTag) === "NL") {
          const bicCode = allValues.coordinates.iban?.replace(/\s/g, "").substr(4, 4)
          const bicNl = getBicNl()
          if (bicNl[bicCode]) {
            return bicNl[bicCode]
          }
        }
        return allValues.coordinates.bic
      },
      "coordinates.bicDisabled": (ignoredValue, allValues) => {
        if (getCountryFromLanguage(tunnelData.data.order.orderLanguageTag) === "NL") {
          const bicCode = allValues.coordinates.iban?.replace(/\s/g, "").substr(4, 4)
          if (getBicNl()[bicCode]) {
            return true
          }
        }
        return false
      }
    }
  }
}

const maritalStatusUseDecorator = () => {
  return {
    field: "situation.maritalStatus",
    updates: {
      "situation.maidenName": (_maritalStatusFieldValue, allValues) => {
        const { maritalStatus, maidenName } = allValues.situation
        return maritalStatus?.value === MARIED_VALUE ? maidenName : null
      }
    }
  }
}

export default (tunnelData) => {
  return createDecorator(
    postalCodeDecorator(),
    cityDecorator(tunnelData),
    citywithPostalCodeDecorator(),
    cityLavoroDecorator(tunnelData),
    birthCountryDecorator(),
    birthDepartmentDecorator(),
    fisalNumberDecorator(tunnelData),
    citizenShipSimplifiedDecorator(),
    phoneNumberDecorator(),
    identityDocumentPlaceOfIssueDecorator(tunnelData),
    identityDocumentsDriverLicenseNumberDecorator(tunnelData),
    professionDecorator(tunnelData),
    workingSituationDecorator(),
    netMonthlyIncomeDecorator(),
    numberMonthsIncomeDecorator(tunnelData),
    ibanDecorator(tunnelData),
    professionalUseDecorator(tunnelData),
    sectorwithProfessionDecorator(tunnelData),
    sectorDecorator(tunnelData),
    detailsIncomeDecorator(),
    showCompanyNameDecorator(),
    showContryActivityInfosUseDecorator(),
    showLineOfBusinessUseDecorator(),
    showProfessionelUseDecorator(),
    showProfessionDecorator(),
    enableIngressoDecorator(),
    showIncomeOriginDecorator(),
    showDetailsIncomeDecorator(),
    maritalStatusUseDecorator()
  )
}
